import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { notificationAPI } from '../services/notificationAPI';
import { NotificationContext } from '../hooks/useNotifications';

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const userId = user?.id;
  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  const currentUser = useRef(userId);
  const requestVersion = useRef(0);
  const refreshCount = useCallback(async () => {
    if (!userId || currentUser.current !== userId) return;
    const version = ++requestVersion.current;
    try {
      const response = await notificationAPI.count();
      if (currentUser.current === userId && version === requestVersion.current) {
        setUnreadCount(response.data.unread_count);
      }
    } catch {
      // Unknown count is not a claim that the inbox is empty.
      if (currentUser.current === userId && version === requestVersion.current) setUnreadCount(null);
    }
  }, [userId]);

  useEffect(() => {
    currentUser.current = userId;
    setUnreadCount(null);
    void refreshCount();
    const refreshVisible = () => { if (document.visibilityState === 'visible') void refreshCount(); };
    const interval = window.setInterval(refreshVisible, 60_000);
    window.addEventListener('focus', refreshVisible);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      currentUser.current = undefined;
      requestVersion.current += 1;
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshVisible);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [userId, refreshCount]);

  return <NotificationContext.Provider value={{ unreadCount, refreshCount }}>{children}</NotificationContext.Provider>;
};
